import { useCallback, useEffect } from 'react';
import { onlineManager, useQueryClient } from '@tanstack/react-query';
import { ChatFileMetadata, ProcessFilesCallback, SelectModelType } from '../types/chat-store.types';
import { useGetConversationById } from './use-conversation-api';
import { useGetAgentConversationSessionById } from './use-agent-conversation';
import { Conversation } from '../types/conversation.service.type';
import { useProcessFiles } from './use-agents';
import { useChatStore } from './use-chat-store';

interface UseChatSSE {
  chatId?: string;
  agentId?: string | null;
  widgetId?: string | null;
  isPreview?: boolean;
}

const projectKey = import.meta.env.VITE_X_BLOCKS_KEY || '';
const projectSlug = import.meta.env.VITE_PROJECT_SLUG || '';

export const useChatSSE = ({
  chatId = '',
  agentId = null,
  widgetId = null,
  isPreview = false,
}: UseChatSSE) => {
  const queryClient = useQueryClient();
  const {
    chats,
    loadChat,
    loadAgentChat,
    generateBotMessage: generateFromStore,
    sendMessage: sendFromStore,
    setSelectedModel,
    setSelectedTools,
  } = useChatStore();
  const activeChatId = useChatStore((state) => state.resolveChatId(chatId || ''));
  const chat = chats[activeChatId] || {
    sessionId: '',
    conversations: [],
    isBotStreaming: false,
    isBotThinking: false,
    pendingSend: false,
  };
  const sessionId = chat.sessionId || '';
  const conversations = chat.conversations || [];
  const isBotStreaming = chat.isBotStreaming || false;
  const isBotThinking = chat.isBotThinking || false;
  const currentEvent = chat?.currentEvent || null;

  // Determine if it's an agent chat from the store's selectedModel
  const isAgentChat = chat?.selectedModel?.provider === 'agents' || !!agentId;
  const { mutateAsync: processFilesMutation } = useProcessFiles();

  // Create callback wrapper for the store to use
  const processFilesCallback: ProcessFilesCallback = useCallback(
    async (params) => {
      try {
        const result = await processFilesMutation(params);
        return result;
      } catch (error) {
        return {
          success: false,
          message: error instanceof Error ? error.message : 'Unknown error',
        };
      }
    },
    [processFilesMutation]
  );

  const { data: modelChatData, isLoading: isLoadingModelChat } = useGetConversationById({
    allow_created_by_filter: true,
    is_minimal: false,
    call_from: projectSlug,
    project_key: projectKey,
    session_id: activeChatId,
    limit: 100,
    offset: 0,
    enabled: !isPreview && !isAgentChat && activeChatId !== 'new',
  });

  const { data: agentChatData, isLoading: isLoadingAgentChat } =
    useGetAgentConversationSessionById({
      allow_created_by_filter: true,
      project_key: projectKey,
      session_id: activeChatId,
      agent_id: agentId || '',
      limit: 100,
      offset: 0,
      enabled: !isPreview && isAgentChat && activeChatId !== 'new',
    });

  const data = isAgentChat ? agentChatData : modelChatData;
  const isLoading = isAgentChat ? isLoadingAgentChat : isLoadingModelChat;

  useEffect(() => {
    if (!activeChatId || activeChatId === 'new' || !data || data.total_count === 0) {
      return;
    }

    // Only load if chat doesn't exist in store or has no conversations
    // This preserves local data (including files) when navigating back
    const hasExistingConversations = chats[activeChatId]?.conversations?.length > 0;
    if (hasExistingConversations) {
      return;
    }

    const conversationData = data.sessions;
    if (isAgentChat && agentId) {
      loadAgentChat(
        activeChatId,
        conversationData as Conversation[],
        agentId,
        widgetId || undefined
      );
    } else {
      loadChat(activeChatId, conversationData as Conversation[]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeChatId, data]);

  // For preview mode, ensure the chat entry exists with the correct agent model
  // so createSSEHandler routes to agentService.agentChatStream
  useEffect(() => {
    if (!isPreview || !activeChatId || !agentId || !widgetId) return;

    const currentChat = useChatStore.getState().chats[activeChatId];
    if (!currentChat || currentChat.selectedModel?.provider !== 'agents') {
      setSelectedModel(activeChatId, {
        isBlocksModels: false,
        provider: 'agents',
        model: agentId,
        widget_id: widgetId,
      });
    }
  }, [isPreview, activeChatId, agentId, widgetId, setSelectedModel]);

  const generateBotMessage = useCallback(
    async (data: {
      message: string;
      setSuggestions?: (suggestions: string[]) => void;
      files?: ChatFileMetadata[];
    }) => {
      await generateFromStore(
        activeChatId,
        data.message,
        data.setSuggestions,
        data.files,
        false,
        processFilesCallback,
        queryClient
      );
    },
    [activeChatId, generateFromStore, processFilesCallback, queryClient]
  );

  const sendMessage = useCallback(
    async (data: { message: string; files?: ChatFileMetadata[] }) => {
      // Ensure agent model is set before sending, to prevent addUserMessage
      // from creating the chat entry with the default azure model.
      // Also set activeChatId so handleSSEMessage writes responses to the correct chat.
      if (isPreview && agentId && widgetId) {
        setSelectedModel(activeChatId, {
          isBlocksModels: false,
          provider: 'agents',
          model: agentId,
          widget_id: widgetId,
        });
        useChatStore.setState({ activeChatId });
      }
      await sendFromStore(
        activeChatId,
        data.message,
        data.files,
        processFilesCallback,
        queryClient
      );
    },
    [
      activeChatId,
      sendFromStore,
      processFilesCallback,
      queryClient,
      isPreview,
      agentId,
      widgetId,
      setSelectedModel,
    ]
  );

  const onModelChange = useCallback(
    (model: SelectModelType) => setSelectedModel(activeChatId, model),
    [activeChatId, setSelectedModel]
  );

  const onToolsChange = useCallback(
    (tools: string[]) => {
      setSelectedTools(activeChatId, tools);
    },
    [activeChatId, setSelectedTools]
  );

  const isOnline = onlineManager.isOnline;
  const isReady =
    isPreview || chatId === 'new' || (chat && chat.conversations.length > 0) ? true : !isLoading;
  const { selectedModel, selectedTools } = chat;
  return {
    sessionId,
    sendMessage,
    conversations,
    isBotThinking,
    isBotStreaming,
    selectedModel,
    selectedTools,
    onModelChange,
    onToolsChange,
    generateBotMessage,
    currentEvent,
    isOnline,
    isReady,
  };
};
